/* eslint-disable @typescript-eslint/no-explicit-any */
import { datadogLogs } from "@datadog/browser-logs";
import { RECAPTCHA_HEADERS, RECAPTCHA_RETRY_ALLOWED } from "@simplyk/common";
import { serializeError } from "serialize-error";
import { v4 } from "uuid";

import { isTest } from "../constants/env";
import { RecaptchaAction } from "../constants/recaptcha";
import {
  GrecaptchaEnterprise,
  RecaptchaTimeoutError,
  requestRecaptchaToken,
  shouldRetryRecaptchaRejection,
} from "../helpers/recaptchaToken";

type RecaptchaFailureCause =
  | "script_tag_not_in_dom"
  | "network_request_blocked"
  | "network_response_empty"
  | "network_slow"
  | "script_loaded_but_not_executed";

interface RecaptchaTokenOutcome {
  token: string | null | undefined;
  hasTimedOut: boolean;
}

interface RecaptchaResourceEntry {
  name: string;
  duration: number;
  decodedBodySize: number;
  initiatorType: string;
  startTime: number;
}

interface RecaptchaScriptDiagnostics {
  scriptTagInDom: boolean;
  resourceEntries: RecaptchaResourceEntry[];
  documentReadyState: DocumentReadyState;
  timeSincePageLoad: number;
  likelyCause: RecaptchaFailureCause;
}

const determineFailureCause = (
  scriptTagInDom: boolean,
  resourceEntries: RecaptchaResourceEntry[]
): RecaptchaFailureCause => {
  if (!scriptTagInDom) {
    return "script_tag_not_in_dom";
  }

  if (resourceEntries.length === 0) {
    return "network_request_blocked";
  }

  const hasReceivedContent = resourceEntries.some((e) => e.decodedBodySize > 0);
  if (!hasReceivedContent) {
    return "network_response_empty";
  }

  const hasSlowRequest = resourceEntries.some((e) => e.duration > 10000);
  if (hasSlowRequest) {
    return "network_slow";
  }

  return "script_loaded_but_not_executed";
};

const getRecaptchaScriptDiagnostics = (): RecaptchaScriptDiagnostics => {
  // Guard against SSR or restricted environments
  if (typeof document === "undefined" || typeof performance === "undefined") {
    return {
      scriptTagInDom: false,
      resourceEntries: [],
      documentReadyState: "loading",
      timeSincePageLoad: 0,
      likelyCause: "script_tag_not_in_dom",
    };
  }

  const scriptTagInDom = !!document.querySelector('script[src*="recaptcha"]');

  const resourceEntries =
    typeof performance.getEntriesByType === "function"
      ? (performance.getEntriesByType("resource") as PerformanceResourceTiming[])
          .filter((e) => e.name.includes("recaptcha") || e.name.includes("gstatic"))
          .map((e) => ({
            name: e.name,
            duration: Math.round(e.duration),
            decodedBodySize: e.decodedBodySize || 0,
            initiatorType: e.initiatorType,
            startTime: Math.round(e.startTime),
          }))
      : [];

  return {
    scriptTagInDom,
    resourceEntries,
    documentReadyState: document.readyState,
    timeSincePageLoad: Math.round(performance.now()),
    likelyCause: determineFailureCause(scriptTagInDom, resourceEntries),
  };
};

export const getGoogleRecaptcha = (): GrecaptchaEnterprise | undefined => {
  // type-coverage:ignore-next-line
  return (window as any).grecaptcha?.enterprise;
};

export const useRecaptchaRetry = (onRecaptchaScriptNotLoaded?: () => void) => {
  const getRecaptchaToken = async (
    action: RecaptchaAction,
    browserErrorEmail: string | undefined | null,
    retryCount = 0
  ): Promise<RecaptchaTokenOutcome> => {
    try {
      if (isTest) {
        return { token: "test", hasTimedOut: false };
      }

      const grecaptcha = getGoogleRecaptcha();

      if (!grecaptcha || !grecaptcha.ready) {
        const diagnostics = getRecaptchaScriptDiagnostics();

        datadogLogs.logger.error("[useRecaptchaRetry] Recaptcha script not loaded", {
          browserErrorEmail: browserErrorEmail || "undefined",
          recaptchaIsPresentInWindow: (!!grecaptcha).toString(),
          recaptchaReadyIsPresentInWindow: (!!grecaptcha?.ready).toString(),
          ...diagnostics,
        });

        if (onRecaptchaScriptNotLoaded) {
          onRecaptchaScriptNotLoaded();
        }

        return { token: undefined, hasTimedOut: false };
      }

      return { token: await requestRecaptchaToken(grecaptcha, action), hasTimedOut: false };
    } catch (error) {
      if (error instanceof RecaptchaTimeoutError) {
        datadogLogs.logger.error("[useRecaptchaRetry] Recaptcha did not answer in time", {
          browserErrorEmail: browserErrorEmail || "undefined",
          error: serializeError(error),
          ...getRecaptchaScriptDiagnostics(),
        });
        return { token: null, hasTimedOut: true };
      }
      if (retryCount < RECAPTCHA_RETRY_ALLOWED) {
        return getRecaptchaToken(action, browserErrorEmail, retryCount + 1);
      }
      datadogLogs.logger.error("[useRecaptchaRetry] Not able to fetch recaptcha token after retries", {
        browserErrorEmail: browserErrorEmail || "undefined",
        error: serializeError(error),
      });
      return { token: null, hasTimedOut: false };
    }
  };

  const executeTrpc = async <T, K>(
    action: RecaptchaAction,
    browserErrorEmail: string | undefined | null,
    request: (args: K) => T & { error?: { code?: string; message?: string } },
    args: K,
    retryCount: number = 0,
    onRecaptchaScriptNotLoaded?: () => void,
    recaptchaAttemptId: string = v4()
  ): Promise<T & { error?: { code?: string; message?: string } }> => {
    const recaptchaScriptStartTime = Date.now();
    const { token: recaptchaToken, hasTimedOut } = await getRecaptchaToken(action, browserErrorEmail, retryCount);
    const recaptchaScriptEndTime = Date.now();
    const recaptchaHeaders = {
      [RECAPTCHA_HEADERS.TOKEN]: recaptchaToken || "",
      [RECAPTCHA_HEADERS.MISSING_SCRIPT]: (!getGoogleRecaptcha()?.execute).toString(),
      [RECAPTCHA_HEADERS.SCRIPT_START_TIME]: recaptchaScriptStartTime.toString(),
      [RECAPTCHA_HEADERS.SCRIPT_END_TIME]: recaptchaScriptEndTime.toString(),
      [RECAPTCHA_HEADERS.RETRY_COUNT]: retryCount.toString(),
      [RECAPTCHA_HEADERS.ATTEMPT_ID]: recaptchaAttemptId,
      [RECAPTCHA_HEADERS.BROWSER_ERROR_EMAIL]: browserErrorEmail || "",
    };

    const res = await request({ ...args, recaptchaHeaders });

    const error = res.error;
    if (!error) {
      return res;
    }

    if (shouldRetryRecaptchaRejection({ errorMessage: error.message, hasTimedOut, retryCount })) {
      const bypassVerificationEmail = "browser_error_success@mailinator.com";
      const newBrowserErrorEmail = browserErrorEmail === bypassVerificationEmail ? undefined : browserErrorEmail;
      return executeTrpc(
        action,
        newBrowserErrorEmail,
        request,
        args,
        retryCount + 1,
        onRecaptchaScriptNotLoaded,
        recaptchaAttemptId
      );
    }

    return res;
  };

  return { executeTrpc };
};
